/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { DiConfigService } from '@app/di/di-config.service';
import { UtilService } from '@app/util/util.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { AppConfigService } from '@app/common/app-config.service';
import { NotifyService } from '@app/common/notify.service';
import { Logger, ModalService } from '@core/lib/services';
import { MessageService } from '@core/lib/services/message.service';
import { appConstants } from '@app/common/app-constants';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const log = new Logger('IntegrationStatusService');

@Injectable({
  providedIn: 'root'
})
export class IntegrationStatusService {
  private dictationModalPreference = false;
  private maxTabInstance;
  private notificationModal;

  availableIntegrations = {
    uv: 'uv',
    ps: 'ps',
    generic: 'generic',
    uvNav: 'uvNav'
  };

  launchStatus = {
    viewer: false
  };

  healthStatusEnum = {
    ready: 'ready',
    pendingResponse: 'pendingResponse',
    lastActionFailed: 'lastActionFailed',
    hideAll: 'hideAll',
    failed: 'failed',
    uvsessionPending: 'uvsessionPending',
    iSiteLoginFailed: 'iSiteLoginFailed',
    unsupportedLanguage: 'unsupportedLanguage'
  };

  integrationAction = {
    timeoutoccurred: 'timeout',
    open: 'open',
    close: 'close',
    hide: 'hide',
    context: 'CURRENTCONTEXT'
  };

  viewerStatus = {
    uView: undefined,
    navigation: undefined
  };

  messageType = {
    viewer: 0,
    report: 1,
    viewerandreport: 2
  };

  reportStatus = undefined;
  ccowStatus = undefined;
  appLauncherStatus = undefined;
  pendingAction = undefined;

  pluginInitilizationStatus = {
    loadViewerPlugin: false,
    loadCCOWPlugin: false,
    loadReportingPlugin: false,
    ccowClearPatientContext: false,
    loadViewerContextPlugin: false,
    loadUVSessionFailed: false,
    loadAppLauncherPlugin: false,
    loadWFMContextPlugin: false
  };

  constructor(
    private translate: TranslateService,
    private diConfigService: DiConfigService,
    private appConfigService: AppConfigService,
    private userPrivilegesService: UserPrivilegesService,
    private utilService: UtilService,
    private notify: NotifyService,
    private messageService: MessageService,
    private modalService: ModalService
  ) {}

  setUVLaunchStatus(launched) {
    this.launchStatus.viewer = launched;
  }

  getUVLaunchStatus() {
    return this.launchStatus.viewer;
  }

  canOpenNewTab(id, isExternalCheckRequired?) {
    const isSupportMoreTabs = this.appConfigService.getAppConfigByKey('unlimitedTabs');
    if (isSupportMoreTabs === 'true') {
      return true;
    } else {
      if (
        this.utilService.getTabsLength() === UtilService.maximumTabsAllowed &&
        this.utilService.getTabIndex(id) === -1
      ) {
        const tab = this.utilService.getExternalTab();
        if (isExternalCheckRequired && tab && tab.id === appConstants.externalTabId) {
          return true;
        }
        if (!this.maxTabInstance) {
          this.openMaximumTabsNotifier();
        }
        return false;
      }
      return true;
    }
  }

  openMaximumTabsNotifier() {
    log.debug(
      'DiagnosticHubTabController - openMaximumTabsNotifier() - opening modal to inform user that ' +
        'they have reached the maximum number of tabs'
    );
    let modalInstance = this.modalService.openModal(
      this.translate.instant('tabs.exceededMaxTabsMsg').replace(/maxTab#/g, UtilService.maximumTabsAllowed),
      {
        headerContent: this.translate.instant('tabs.maxTabsExceededMsg')
      }
    );

    const sub = modalInstance.afterClosed.subscribe(() => {
      sub.unsubscribe();
      modalInstance = undefined;
    });
  }

  isReportPending() {
    return this.getReportIntegrationEnabled() && this.reportStatus === this.healthStatusEnum.pendingResponse;
  }

  isReportPendingOrUndefined() {
    return (
      this.getReportIntegrationEnabled() &&
      (!this.reportStatus || this.reportStatus === this.healthStatusEnum.pendingResponse)
    );
  }

  isViewerPending() {
    return this.viewerStatus.uView === this.healthStatusEnum.pendingResponse;
  }

  isViewerPendingOrUndefined() {
    return !this.viewerStatus.uView || this.viewerStatus.uView === this.healthStatusEnum.pendingResponse;
  }

  isPendingRequest(newContext?, oldContext?) {
    let currentItem = null;
    if (oldContext) {
      currentItem = this.utilService.getTabWorkItem(oldContext);
    } else {
      currentItem = this.getActiveItem();
    }

    if (this.isViewerPending() || this.isReportPending()) {
      if (currentItem && newContext === currentItem.id) {
        return true;
      }

      this.displayPendingNotificationModal(currentItem);
      return true;
    }
    return false;
  }

  getTimeOut(timeout) {
    return isNaN(timeout) ? 60000 : parseInt(timeout, 10);
  }

  displayPendingNotificationModal(activeItem: any) {
    if (!this.notificationModal) {
      const viewerPending = this.isViewerPending();
      const reportPending = this.isReportPending();
      let messageType;
      let integration;
      if (viewerPending && reportPending) {
        integration = this.translate.instant('di.desktopIntegration.critical.viewerandreporting');
        messageType = this.messageType.viewerandreport;
      } else if (viewerPending) {
        integration = this.translate.instant('di.desktopIntegration.critical.viewer');
        messageType = this.messageType.viewer;
      } else if (reportPending) {
        integration = this.translate.instant('di.desktopIntegration.critical.reportingSystem');
        messageType = this.messageType.report;
      } else {
        return;
      }
      let message = this.translate
        .instant('di.desktopIntegration.critical.pendingResponse')
        .replace('[integration]', integration);

      let showReset = false;
      if (reportPending) {
        const forceCloseTime = this.getTimeOut(this.diConfigService.DI_CONFIG.FORCE_CLOSE_TIMEOUT.report);
        showReset = activeItem
          ? new Date().getTime() - new Date(activeItem.keys.studyClickTime).getTime() > forceCloseTime
          : this.showResetForReporting();
      }
      message = showReset
        ? message + this.translate.instant('di.desktopIntegration.critical.reset')
        : message + this.translate.instant('di.desktopIntegration.critical.tryagain');

      this.notificationModal = this.modalService.openModal(message, {
        headerContent: this.translate.instant('di.desktopIntegration.title'),
        yesButtonLabel: this.translate.instant('label.Reset'),
        noButtonLabel: this.translate.instant('label.OK'),
        isDecisionModal: showReset,
        enableYesBtn: true
      });
      this.notificationModal.messageType = messageType;
      const modalSubscription: Subscription = this.notificationModal.afterClosed.subscribe(data => {
        modalSubscription.unsubscribe();
        if (data === 'YES') {
          if (this.isReportPending()) {
            this.messageService.broadcast('resetIntegration', 'Report');
          }
        }
        this.notificationModal = undefined;
      });
    }
  }

  showResetForReporting() {
    return this.isReportingType(appConstants.reportingAppName.Generic)
      ? this.isReportPending() && this.pendingAction === this.integrationAction.context
      : this.isReportPending();
  }

  getReportingHealthStatus() {
    return this.reportStatus;
  }

  setUViewerPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadViewerPlugin = isLoaded;
  }

  getUViewerPluginStatus() {
    return this.pluginInitilizationStatus.loadViewerPlugin;
  }

  setUViewerNavSyncPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadViewerContextPlugin = isLoaded;
  }

  getUViewerNavSyncPluginStatus() {
    return this.pluginInitilizationStatus.loadViewerContextPlugin;
  }

  setAppLauncherPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadAppLauncherPlugin = isLoaded;
  }

  getAppLauncherPluginStatus() {
    return this.pluginInitilizationStatus.loadAppLauncherPlugin;
  }

  setReportingPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadReportingPlugin = isLoaded;
  }

  getReportingPluginStatus() {
    return this.pluginInitilizationStatus.loadReportingPlugin;
  }

  setCCOWPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadCCOWPlugin = isLoaded;
  }

  getCCOWPluginStatus() {
    return this.pluginInitilizationStatus.loadCCOWPlugin;
  }

  setWFMContextPluginStatus(isLoaded) {
    this.pluginInitilizationStatus.loadWFMContextPlugin = isLoaded;
  }

  getWFMContextPluginStatus() {
    return this.pluginInitilizationStatus.loadWFMContextPlugin;
  }

  getUViewerSessionFailed() {
    return this.pluginInitilizationStatus.loadUVSessionFailed;
  }

  setUViewerSessionFailed(isLoaded) {
    this.pluginInitilizationStatus.loadUVSessionFailed = isLoaded;
  }

  setCCOWClearPatientContext(isLoaded) {
    this.pluginInitilizationStatus.ccowClearPatientContext = isLoaded;
  }

  getCCOWClearPatientContext() {
    return this.pluginInitilizationStatus.ccowClearPatientContext;
  }

  setReportingHealthStatus(status, action?) {
    this.reportStatus = this.getReportingPluginStatus() ? status : this.reportStatus;
    this.pendingAction = action;
    this.messageService.broadcast('integralStatusBoardUpdate', ['reporting', this.reportStatus]);
    this.checkAndCloseNotificationModal();
  }

  getPendingAction() {
    return this.pendingAction;
  }

  setPendingAction(action) {
    this.pendingAction = action;
  }

  setViewerHealthStatus(status, isUV?) {
    if (isUV) {
      if (this.isUvActive()) {
        this.viewerStatus.uView = this.getUViewerPluginStatus() ? status : this.viewerStatus.uView;

        if (this.viewerStatus.uView) {
          this.messageService.broadcast('integralStatusBoardUpdate', [
            this.availableIntegrations.uv,
            this.viewerStatus.uView
          ]);
        }
      }
    }
    this.checkAndCloseNotificationModal();
  }

  checkAndCloseNotificationModal() {
    if (this.notificationModal) {
      let closeModal = false;
      if (this.notificationModal.messageType === this.messageType.viewerandreport) {
        closeModal = this.viewerStatus.uView !== 'pendingResponse' && this.reportStatus !== 'pendingResponse';
      } else if (this.notificationModal.messageType === this.messageType.viewer) {
        closeModal = this.viewerStatus.uView !== 'pendingResponse';
      } else if (this.notificationModal.messageType === this.messageType.report) {
        closeModal = this.reportStatus !== 'pendingResponse';
      }
      if (closeModal) {
        this.messageService.broadcast('close.modalRef');
        this.notificationModal = undefined;
      }
    }
  }

  getViewerHealthStatus() {
    return this.viewerStatus.uView;
  }

  resetUViewerHealthStatus() {
    this.viewerStatus.uView = undefined;
  }

  isUvActive() {
    if (this.viewerStatus.uView === 'failed') {
      return false;
    }
    return true;
  }

  isReportActive() {
    return this.reportStatus !== 'failed';
  }

  setViewerSyncHealthStatus(status) {
    this.viewerStatus.navigation = status;
    this.messageService.broadcast('integralStatusBoardUpdate', [this.availableIntegrations.uvNav, status]);
  }

  getViewerSyncHealthStatus() {
    return this.viewerStatus.navigation;
  }

  setAppLauncherHealthStatus(status) {
    this.appLauncherStatus = status;
  }

  getAppLauncherHealthStatus() {
    return this.appLauncherStatus;
  }

  disableDesktopIntegration() {
    this.messageService.broadcast('diDisableIntegration');

    // If the plgin load is failed after retrying, no need to load the plugin.
    this.pluginInitilizationStatus.loadViewerPlugin = true;
    this.pluginInitilizationStatus.loadCCOWPlugin = true;
    this.pluginInitilizationStatus.loadViewerContextPlugin = true;
    this.pluginInitilizationStatus.loadReportingPlugin = true;

    this.setViewerHealthStatus(this.healthStatusEnum.failed, true);
    this.setViewerHealthStatus(this.healthStatusEnum.failed);

    this.setReportingHealthStatus(this.healthStatusEnum.failed);
    this.setViewerSyncHealthStatus(this.healthStatusEnum.failed);
  }

  getTabIndex(procedureid) {
    return this.utilService.getTabIndex(procedureid);
  }

  getActiveItem() {
    const tab = this.utilService.getActiveTab();
    if (tab) {
      return tab.workItem;
    }
    log.info('IntegrationStatus - getActiveItem() - existing tab not found');
    return undefined;
  }

  getReportIntegrationEnabled = () => {
    const DI_CONFIG = this.diConfigService.DI_CONFIG;
    return (
      DI_CONFIG.REPORTING_INTEGRATION_TYPE &&
      DI_CONFIG.REPORTING_INTEGRATION_TYPE !== appConstants.reportingAppName.None &&
      (this.userPrivilegesService.hasPrivilege('Dictate') || this.userPrivilegesService.hasPrivilege('ID_NPR'))
    );
  };

  setDictateModalPreference(value) {
    this.dictationModalPreference = value;
  }

  getDictateModalPreference() {
    return this.dictationModalPreference;
  }

  isReportingType(type: string) {
    return type === this.diConfigService.DI_CONFIG?.REPORTING_INTEGRATION_TYPE;
  }

  isNativeReportingEnabled() {
    return this.diConfigService.DI_CONFIG?.REPORTING_INTEGRATION_TYPE === appConstants.reportingAppName.NPR;
  }

  isLineShaftIntegrationEnabled() {
    const reportingType = this.diConfigService.DI_CONFIG?.REPORTING_INTEGRATION_TYPE;
    return !!(
      reportingType &&
      reportingType !== appConstants.reportingAppName.None &&
      reportingType !== appConstants.reportingAppName.NPR
    );
  }

  // These 2 new APIs should be added to system-status.service.ts when it is available
  addSystemError(type: string, message: string) {
    this.appConfigService.getSystemStatusNotifier().next({ key: type, message: message });
  }

  clearSystemError(type: string) {
    this.appConfigService.getSystemStatusNotifier().next({ key: type, message: '' });
  }
}
